<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import { Button, Input, Modal, Select } from "@/components/ui";
import { Icon } from "@iconify/vue";
import { ProductPriceTypes, ProductPurchaseTypes } from "@/utils/enums";
import { ErrorMessage, Field, Form, defineRule } from "vee-validate";
import { useProductNames } from "@/composables/product";

defineRule("required", (value, [fieldName = "Field"]) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

// Separate from "required" so a valid $0 price (allowed for fixed-price products) isn't
// rejected by the falsy check above.
defineRule("requiredNumber", (value, [fieldName = "Field"]) => {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
    return `${fieldName} is required`;
  }
  return true;
});

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["closeAddProductModal", "addProduct"]);

const store = useStore();
const account = computed(() => store.getters.account.data);

const registerProductObj = ref({
  name: null,
  price: null,
  quantity: 1,
  tax: null,
  purchaseType: null,
  organizationId: null,
});

const purchaseTypeOptions = computed(() => {
  return Object.values(ProductPurchaseTypes).map((type) => ({
    value: type,
    label: type,
  }));
});

// Product name catalog (organization-scoped, for the "select existing product" dropdown)
const productNamesParams = computed(() => {
  if (!account.value?.organizationId) return null;
  return { organizationId: account.value.organizationId };
});
const { data: productNamesData, isLoading: productNamesLoading } =
  useProductNames(productNamesParams);
const productNameCatalog = computed(() => productNamesData.value || []);

const productNameOptions = computed(() => {
  return productNameCatalog.value
    .filter((product) => product.isActive)
    .map((product) => ({
      value: product.name,
      label: product.name,
    }));
});

const findCatalogProductByName = (name) => {
  return productNameCatalog.value.find((product) => product.name === name);
};

// A "fixed" priced product (set in Settings > Products) locks the Price field —
// reps can't override it on the deal. "Flexible" (or a brand-new name) stays editable.
const selectedCatalogProduct = computed(() =>
  findCatalogProductByName(registerProductObj.value.name)
);
const isPriceLocked = computed(
  () => selectedCatalogProduct.value?.priceType === ProductPriceTypes.fixed
);

const isCreatingNewProductName = ref(false);
const newProductNameInput = ref(null);

const onProductNameSelected = (name, field) => {
  field.onChange(name);
  const match = findCatalogProductByName(name);
  if (match) {
    registerProductObj.value.price = match.price ?? null;
  }
};

const startCreatingNewProduct = () => {
  isCreatingNewProductName.value = true;
  registerProductObj.value.name = null;
  // Don't carry over a price (possibly locked) that belonged to the previously selected catalog product.
  registerProductObj.value.price = null;
  nextTick(() => newProductNameInput.value?.focus?.());
};

const cancelCreatingNewProduct = () => {
  isCreatingNewProductName.value = false;
  registerProductObj.value.name = null;
};

const closeModal = () => {
  emit("closeAddProductModal");
};

const registerProduct = () => {
  if (props.loading) return;
  emit("addProduct", registerProductObj.value);
};

onMounted(() => {
  if (account.value) {
    registerProductObj.value.organizationId = account.value.organizationId;
  }
});
</script>

<template>
  <Modal
    :show="true"
    headerVariant="blue"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.15"
    :show-close-button="false"
    :zIndex="10090"
    size="md"
    @close="closeModal"
  >
    <template #header>
      <div class="product-header__text">
        <h2 class="product-header__title">Add Product</h2>
        <p class="product-header__subtitle">Add a new product</p>
      </div>
      <button type="button" class="product-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>

    <template #body>
      <Form id="add-product-form" @submit="registerProduct">
        <div class="product-form">
          <div class="product-form__row">
            <div class="product-form__field">
              <Field
                v-slot="{ field }"
                name="name"
                :rules="{ required: ['Name'] }"
                :model-value="registerProductObj.name"
                @update:model-value="registerProductObj.name = $event"
              >
                <Input
                  v-if="isCreatingNewProductName"
                  ref="newProductNameInput"
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  label="Name"
                  type="text"
                  :required="true"
                  placeholder="Enter new product name"
                />
                <Select
                  v-else
                  :model-value="field.value"
                  @update:model-value="onProductNameSelected($event, field)"
                  @blur="field.onBlur"
                  :options="productNameOptions"
                  :loading="productNamesLoading"
                  label="Name"
                  :required="true"
                  placeholder="Select or search product"
                  searchable
                  search-placeholder="Search products..."
                  show-add-button
                  add-button-text="New product name"
                  @add-new="startCreatingNewProduct"
                />
              </Field>
              <button
                v-if="isCreatingNewProductName"
                type="button"
                class="product-form__back-link"
                @click="cancelCreatingNewProduct"
              >
                <Icon icon="tabler:corner-up-left" width="12" height="12" />
                Choose from existing products
              </button>
              <ErrorMessage name="name" class="error-message" />
            </div>
            <div class="product-form__field">
              <Field
                v-slot="{ field }"
                name="price"
                :rules="{ requiredNumber: ['Price'] }"
                :model-value="registerProductObj.price"
                @update:model-value="registerProductObj.price = $event"
              >
                <Input
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  label="Price"
                  type="number"
                  :required="true"
                  :disabled="isPriceLocked"
                  placeholder="0.00"
                />
              </Field>
              <div v-if="isPriceLocked" class="product-form__lock-hint">
                <Icon icon="tabler:lock" width="11" height="11" />
                Fixed price — set in Settings
              </div>
              <ErrorMessage name="price" class="error-message" />
            </div>
          </div>
          <div class="product-form__row">
            <div class="product-form__field">
              <Field
                v-slot="{ field }"
                name="quantity"
                :model-value="registerProductObj.quantity"
                @update:model-value="registerProductObj.quantity = $event"
              >
                <Input
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  label="Quantity"
                  type="number"
                  placeholder="1"
                />
              </Field>
            </div>
            <div class="product-form__field">
              <Field
                v-slot="{ field }"
                name="tax"
                :model-value="registerProductObj.tax"
                @update:model-value="registerProductObj.tax = $event"
              >
                <Input
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  label="Tax %"
                  type="number"
                  placeholder="0.00"
                />
              </Field>
            </div>
          </div>
          <div>
            <Field
              name="purchaseType"
              :rules="{ required: ['Subscription Type'] }"
              :model-value="registerProductObj.purchaseType"
              @update:model-value="registerProductObj.purchaseType = $event"
              v-slot="{ field }"
            >
              <Select
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                :options="purchaseTypeOptions"
                label="Subscription Type"
                :required="true"
                placeholder="Select subscription type"
              />
            </Field>
            <ErrorMessage name="purchaseType" class="error-message" />
          </div>
        </div>
      </Form>
    </template>

    <template #footer>
      <Button variant="secondary" size="sm" @click="closeModal" :disabled="loading">
        Cancel
      </Button>
      <Button variant="primary" size="sm" type="submit" form="add-product-form" :loading="loading">
        Add Product
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.product-form {
  padding: 4px 20px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.product-form__row {
  display: flex;
  gap: 16px;
}

.product-form__field {
  flex: 1;
}

.error-message {
  margin-top: 4px;
  display: block;
  font-size: 11px;
  color: #dc3545;
}
.product-form__back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 0;
  background: none;
  border: none;
  color: #3e3efe;
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
}
.product-form__back-link:hover {
  text-decoration: underline;
}
.product-form__lock-hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  color: #9ca3af;
  font-size: 11px;
}
.product-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.product-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}
.product-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}
.product-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.product-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}
</style>
